// Material Design colors - they look decent
const supplyColors = [
  '#2196F3', '#FF9800', '#4CAF50', '#E91E63', '#9C27B0', '#FF5722',
  '#00BCD4', '#795548', '#607D8B', '#FFC107', '#3F51B5', '#8BC34A'
];
export function processTransportSolution(apiResponse, supplies, demands, locations) {
  if (!apiResponse) {
    return {
      sidebarData: null,
      mapData: null,
      isolationInfo: null
    };
  }

  // Just in case someone passes null/undefined
  if (!supplies || !Array.isArray(supplies)) supplies = [];
  if (!demands || !Array.isArray(demands)) demands = [];
  if (!locations || !Array.isArray(locations)) locations = [];

  // 1. Process sidebar data (maintain original data integrity)
  const sidebarData = {
    ...apiResponse,
    // Additional processing logic can be added here
    processedAt: new Date().toISOString()
  };

  // 2. Analyze isolation information
  const isolationInfo = analyzeIsolation(apiResponse.allocations || [], supplies, demands);

  // 3. Process map data
  const mapData = createMapVisualizationData(apiResponse.routes || [], supplies, demands, locations);

  // 4. Generate route analysis information
  const routeAnalysis = generateRouteAnalysis(apiResponse.routes || [], supplies, demands);

  return {
    sidebarData,
    mapData,
    isolationInfo,
    routeAnalysis,
    // Metadata
    metadata: {
      totalSupplies: supplies.length,
      totalDemands: demands.length,
      totalRoutes: apiResponse.routes?.length || 0,
      isBalanced: Math.abs(
        supplies.reduce((sum, s) => sum + Number(s.quantity || 0), 0) -
        demands.reduce((sum, d) => sum + Number(d.quantity || 0), 0)
      ) < 0.01
    }
  };
}

/**
 * Create map visualization data
 */
function createMapVisualizationData(routes, supplies, demands, locations) {
  if (!routes || routes.length === 0) {
    return {
      type: 'FeatureCollection',
      features: []
    };
  }

  // Parameter validation
  if (!supplies || !Array.isArray(supplies)) supplies = [];
  if (!demands || !Array.isArray(demands)) demands = [];
  if (!locations || !Array.isArray(locations)) locations = [];


  // Only show real routes, skip virtual ones
  const realRoutes = routes.filter(route => {
    return route.quantity > 0 && 
           route.supply_index < supplies.length && 
           route.demand_index < demands.length;
  });

  // Group routes by supply point for easier processing
  const routesBySupply = {};
  realRoutes.forEach(route => {
    const idx = route.supply_index;
    if (!routesBySupply[idx]) routesBySupply[idx] = [];
    routesBySupply[idx].push(route);
  });

  const features = [];
  
  // Draw lines from each supply to its customers
  Object.entries(routesBySupply).forEach(([supplyIdx, routes]) => {
    const i = parseInt(supplyIdx);
    const supply = supplies[i];
    const routeColor = supplyColors[i % supplyColors.length];

    routes.forEach(route => {
      const flowCoordinates = buildFlowCoordinates(route);
      
      if (flowCoordinates.length >= 2) {
        const actualDemand = route.demand_index < demands.length ? demands[route.demand_index] : null;
        const demandName = actualDemand?.number || actualDemand?.name || route.to_location || `D${route.demand_index + 1}`;
        
        const flowFeature = {
          type: 'Feature',
          properties: {
            supplyIndex: i,
            supply_number: supply?.number || supply?.name || `S${i + 1}`,
            demand_number: demandName,
            quantity: route.quantity,
            distance: route.distance,
            route_color: routeColor,
            pathType: 'auto',
            flowType: 'supply-to-demand'
          },
          geometry: {
            type: 'LineString',
            coordinates: flowCoordinates
          }
        };
        
        features.push(flowFeature);
      }
    });
  });

  return {
    type: 'FeatureCollection',
    features: features
  };
}

/**
 * Build flow coordinates for individual supply-demand pair
 */
function buildFlowCoordinates(route) {
  const coordinates = [];
  
  if (route.path_coordinates && route.path_coordinates.length >= 2) {
    // Use existing path coordinates
    coordinates.push([
      route.path_coordinates[0][1], // lng
      route.path_coordinates[0][0]  // lat
    ]);
    coordinates.push([
      route.path_coordinates[1][1], // lng
      route.path_coordinates[1][0]  // lat
    ]);
  } else if (route.from_location && route.to_location) {
    // Parse location strings
    const fromMatch = route.from_location.match(/\(([^,]+),\s*([^)]+)\)/);
    const toMatch = route.to_location.match(/\(([^,]+),\s*([^)]+)\)/);
    
    if (fromMatch && toMatch) {
      coordinates.push([parseFloat(fromMatch[2]), parseFloat(fromMatch[1])]); // [lng, lat]
      coordinates.push([parseFloat(toMatch[2]), parseFloat(toMatch[1])]);     // [lng, lat]
    }
  }
  
  return coordinates;
}

/**
 * Build path coordinates (deprecated - kept for backward compatibility)
 */
function buildPathCoordinates(supplyRoutes) {
  const pathCoordinates = [];
  
  if (supplyRoutes.length > 0 && supplyRoutes[0].path_coordinates && supplyRoutes[0].path_coordinates.length > 0) {
    const firstRoute = supplyRoutes[0];
    // Starting point
    pathCoordinates.push([
      firstRoute.path_coordinates[0][1], 
      firstRoute.path_coordinates[0][0]
    ]);
    
    // Demand points
    supplyRoutes.forEach(route => {
      if (route.path_coordinates && route.path_coordinates.length >= 2) {
        const demandCoord = route.path_coordinates[1];
        pathCoordinates.push([demandCoord[1], demandCoord[0]]);
      }
    });
  } else {
    // Alternative: use from_location and to_location if available
    if (supplyRoutes.length > 0) {
      const firstRoute = supplyRoutes[0];
      if (firstRoute.from_location) {
        // Parse location string like "(-37.833, 144.915)"
        const fromMatch = firstRoute.from_location.match(/\(([^,]+),\s*([^)]+)\)/);
        if (fromMatch) {
          const startCoord = [parseFloat(fromMatch[2]), parseFloat(fromMatch[1])];
          pathCoordinates.push(startCoord);
          
          supplyRoutes.forEach(route => {
            if (route.to_location) {
              const toMatch = route.to_location.match(/\(([^,]+),\s*([^)]+)\)/);
              if (toMatch) {
                const endCoord = [parseFloat(toMatch[2]), parseFloat(toMatch[1])];
                pathCoordinates.push(endCoord);
              }
            }
          });
        }
      }
    }
  }
  
  return pathCoordinates;
}

/**
 * Analyze isolation situations
 */
function analyzeIsolation(allocations, supplies, demands) {
  if (!allocations || !Array.isArray(allocations) || !supplies || !demands) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[TP] Invalid parameters for analyzeIsolation:', { allocations, supplies, demands });
    }
    return null;
  }
  
  const isolatedSupplies = [];
  const isolatedDemands = [];
  const partiallyUnmetDemands = [];
  const unusedSupplies = [];
  
  // Check supply points
  if (allocations && Array.isArray(allocations)) {
    allocations.forEach((row, supplyIndex) => {
      if (supplyIndex >= supplies.length) return;
    
    const supply = supplies[supplyIndex];
    if (!supply) return; // Skip invalid supply points
    
    const totalAllocated = row.reduce((sum, alloc) => sum + alloc, 0);
    const supplyQuantity = Number(supply.quantity || 0);
    
    if (totalAllocated === 0) {
      isolatedSupplies.push({
        index: supplyIndex,
        location: supply,
        quantity: supplyQuantity,
        allocated: 0,
        reason: 'No demand allocated to this supply point'
      });
    } else if (totalAllocated < supplyQuantity) {
      unusedSupplies.push({
        index: supplyIndex,
        location: supply,
        quantity: supplyQuantity,
        allocated: totalAllocated,
        unused: supplyQuantity - totalAllocated,
        reason: 'Supply partially unused'
      });
    }
    });
  }
  
  // Check demand points
  const demandColumns = (allocations && allocations[0]) ? allocations[0].length : 0;
  for (let demandIndex = 0; demandIndex < Math.min(demandColumns, demands.length); demandIndex++) {
    const demand = demands[demandIndex];
    if (!demand) continue; // Skip invalid demand points
    
    const totalReceived = allocations ? allocations.reduce((sum, row) => sum + (row[demandIndex] || 0), 0) : 0;
    const demandQuantity = Number(demand.quantity || 0);
    
    if (totalReceived === 0) {
      isolatedDemands.push({
        index: demandIndex,
        location: demand,
        quantity: demandQuantity,
        received: 0,
        reason: 'No supply allocated to this demand point'
      });
    } else if (totalReceived < demandQuantity) {
      partiallyUnmetDemands.push({
        index: demandIndex,
        location: demand,
        quantity: demandQuantity,
        received: totalReceived,
        unmet: demandQuantity - totalReceived,
        reason: 'Demand partially unmet'
      });
    }
  }
  
  return {
    isolatedSupplies,
    isolatedDemands,
    partiallyUnmetDemands,
    unusedSupplies,
    hasIsolation: isolatedSupplies.length > 0 || isolatedDemands.length > 0
  };
}


 //Generate route analysis information

function generateRouteAnalysis(routes, supplies, demands) {
  if (!routes) return null;
  
  const virtualSupplyRoutes = routes.filter(route => 
    route.supply_index >= supplies.length && route.quantity > 0
  );
  
  return {
    totalRoutes: routes.length,
    actualSuppliesCount: supplies.length,
    actualDemandsCount: demands.length,
    virtualRoutes: virtualSupplyRoutes,
    realRoutes: routes.filter(route => 
      route.supply_index < supplies.length && route.quantity > 0
    ),
    routesBySupply: groupRoutesBySupply(routes, supplies),
    demandLocationsMapping: demands.map((d, idx) => ({
      index: idx,
      locationNumber: d.number || d.name,
      id: d.id,
      tpType: d.tpType
    }))
  };
}

/**
 * Group routes by supply points
 */
function groupRoutesBySupply(routes, supplies) {
  const grouped = {};
  
  routes.forEach(route => {
    if (route.quantity > 0) {
      const supplyIndex = route.supply_index;
      if (!grouped[supplyIndex]) {
        grouped[supplyIndex] = {
          isVirtual: supplyIndex >= supplies.length,
          routes: []
        };
      }
      grouped[supplyIndex].routes.push(route);
    }
  });
  
  return grouped;
}

/**
 * Validate processing results
 */
export function validateProcessedData(processedData) {
  const errors = [];
  
  if (!processedData.sidebarData) {
    errors.push('Missing sidebar data');
  }
  
  if (!processedData.mapData || !processedData.mapData.features) {
    errors.push('Invalid map data format');
  }
  
  if (processedData.mapData.features.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[TP] No map features generated - this may be normal for problems with no feasible routes');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

// Export constants
export { supplyColors };
